// @flow
import { getIfGlobalDiscountIsImported } from '../../custom-hooks/useGlobalDiscountProvidedByStatus';
import {
  getMetricLevelDiscountRowsFromFormikValues,
  getDimensionRowsFromMetricRow,
  getGlobalDiscountFromFormikValues,
  getIfAtLeastOneMLDOrSGDiscountsExist,
} from '../custom-discounts/utils';

import {
  ACV_DOWN_SELL_WARNING_MESSAGE,
  ARR_GROWTH_POLICY_WARNING_MESSAGE,
  IS_IMPORTED_BACKEND_NAME,
} from './constants';

export const getImportButtonTooltipText = (
  hasMLDOrSGDiscounts,
  shouldImportButtonBeDisabledBecauseOfGD
) => {
  if (hasMLDOrSGDiscounts) {
    return 'Please remove all existing MLD line items for importing!';
  }
  if (shouldImportButtonBeDisabledBecauseOfGD) {
    return 'Importing is disabled because GD is > zero or it is provided by an import already!!';
  }
  return '';
};

export const getIfImportButtonShouldBeDisabled = (values, isGlobalDiscountImported) => {
  const shouldImportButtonBeDisabledBecauseOfMLD = getIfAtLeastOneMLDOrSGDiscountsExist(values);
  const shouldImportButtonBeDisabledBecauseOfGD =
    getGlobalDiscountFromFormikValues(values) > 0 || isGlobalDiscountImported;
  return { shouldImportButtonBeDisabledBecauseOfMLD, shouldImportButtonBeDisabledBecauseOfGD };
};
/**
 * Checks if at least one of the MLD rows is imported
 * Used to determine if the ACV comparison banner should be shown persistently.
 * @param {Object} formikValues - The current Formik form values
 * @returns {boolean} True if at least one MLD row is imported
 */
export const getIfAtleastOneOfTheMLDRowsIsComingFromImportedFile = (formikValues) => {
  const mldRows = getMetricLevelDiscountRowsFromFormikValues(formikValues);
  return mldRows.some((metricRow) => {
    const dimensionRowsFromMetricRow = getDimensionRowsFromMetricRow(metricRow);
    return dimensionRowsFromMetricRow.some(
      (dimensionItem) => dimensionItem[IS_IMPORTED_BACKEND_NAME] === true
    );
  });
};

export const getIfCustomDiscountsOrGlobalDiscountIsImported = (formikValues, estimate) => {
  const hasImportedMLDDiscounts = getIfAtleastOneOfTheMLDRowsIsComingFromImportedFile(formikValues);
  const isGlobalDiscountImported = getIfGlobalDiscountIsImported(estimate);

  return hasImportedMLDDiscounts || isGlobalDiscountImported;
};

export const getPreviousACVFromPreviousTCVAndDealDuration = (
  previousTCV,
  previousDealDurationInYears
) => {
  if (!previousTCV || !previousDealDurationInYears) {
    return null;
  }
  if (previousDealDurationInYears <= 0) {
    return null;
  }
  return previousTCV / previousDealDurationInYears;
};

const ARR_GROWTH_MULTIPLIER = 1.2;

/**
 * Determines which renewal discount validation warning message to show (if any).
 *
 * Validation Rules:
 * 1. If the previous contract ACV is available and the new ACV is lower than the previous contract ACV, show the ACV down-sell warning message.
 * 2. If the previous contract ARR is available and the new ACV is less than 1.2 times the previous contract ARR, show the ARR growth policy warning message.
 * 3. If either the previous contract ACV or ARR is not available, the corresponding validation check is skipped when the required previous-contract value is missing..
 *
 * Priority: ACV down-sell check takes precedence over ARR growth policy check.
 * If the required data (previousContractACV or previousContractARR) is unavailable, that check is skipped.
 *
 * @returns {string|null} The warning message to display, or null if no warning applies.
 */
export const getRenewalDiscountValidationWarningMessage = ({
  estimateACV,
  previousContractACV,
  previousContractARR,
}) => {
  const isPreviousACVAvailable = previousContractACV != null;
  const isPreviousARRAvailable = previousContractARR != null;

  if (isPreviousACVAvailable && estimateACV < previousContractACV) {
    return ACV_DOWN_SELL_WARNING_MESSAGE;
  }
  if (isPreviousARRAvailable && estimateACV < ARR_GROWTH_MULTIPLIER * previousContractARR) {
    return ARR_GROWTH_POLICY_WARNING_MESSAGE;
  }
  return null;
};
